'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ChartNoAxesCombined, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react'
import { useBike } from '../context/BikeContext'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

import { useAuth, api } from "../context/AuthContext"
import { useCart } from "../context/CartContext"
import { useAuthModal } from "../context/AuthModalContext"
import { useWishlist } from "../context/WishlistContext"
import axios from "axios"
import { API } from "../context/AuthContext"
import { isLaunchLocked } from '../utils/productLaunch'
import ProductCard from '../components/ProductCard'
import { toast } from "sonner"
const Home = () => {
  const navigate = useNavigate()
  const { selectedBike, setBikeCompatibility, clearBikeCompatibility } = useBike()

  // State
  const [brands, setBrands] = useState([])
  const [categories, setCategories] = useState([])
  const [products, setProducts] = useState([])
  const [launchingProducts, setLaunchingProducts] = useState([])
  const [bestSellers, setBestSellers] = useState([])
  const [comboOffers, setComboOffers] = useState([])
  const [featuredProducts, setFeaturedProducts] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [newArrivals, setNewArrivals] = useState([])
  const [selectedBrand, setSelectedBrand] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [selectedVariant, setSelectedVariant] = useState('')
  const [models, setModels] = useState([])
  const [variants, setVariants] = useState([])
  const categoryScrollRef = useRef(null)
  const brandScrollRef = useRef(null)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [heroIndex, setHeroIndex] = useState(0)
  const mobileSliderRef = useRef(null)
  const { isAuthenticated } = useAuth()
  const { refreshCartCount } = useCart()
  const { openAuth } = useAuthModal()
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()
  const heroSlides = [
    {
      type: 'video',
      src: '/tank2.mp4',
      poster: '/tank1.jpeg',
      eyebrow: 'Outrans Originals',
      title: 'TANK COVER',
      accent: 'AVAILABLE NOW',
      copy: 'A purpose-built tank cover combining durable construction, rider-focused grip and model-specific styling for a more complete motorcycle.',
      primary: 'Shop Now',
      subtitle: 'Available only through RRide Moto Studio.',
      action: () => navigate('/products?is_outran=true'),
    },
  {
  type: 'image',
  src: '/tank6.jpeg',
  eyebrow: 'Outrans Originals',
  title: 'TANK COVER',
  accent: 'AVAILABLE NOW',
  copy: 'Precision fit. Rugged protection. Premium finish. Now at RRide Moto Studio. OUTRAN Tank Covers, available exclusively at RRide Moto Studio.',
  primary: 'Shop Now',
  subtitle: 'Available only through RRide Moto Studio.',
  action: () => navigate('/products?is_outran=true'),
},
  ]
  // Fetch initial data
  useEffect(() => {
    fetchData()
  }, [])

  // Debug combo offers
  useEffect(() => {
    console.log('Combo offers changed:', comboOffers)
  }, [comboOffers])

  // Fetch models when brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchModels(selectedBrand)
    }
  }, [selectedBrand])
  useEffect(() => {
    if (!brands.length) return

    const interval = setInterval(() => {
      setActiveIndex((prev) =>
        prev === brands.length - 1 ? 0 : prev + 1
      )
    }, 3000)

    return () => clearInterval(interval)
  }, [brands])

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1))
    }, 2000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!mobileSliderRef.current) return

    const cardWidth = 96 // 80px circle + spacing
    mobileSliderRef.current.scrollTo({
      left: activeIndex * cardWidth,
      behavior: "smooth",
    })
  }, [activeIndex])

  // Fetch variants when model changes
  useEffect(() => {
    if (selectedBrand && selectedModel) {
      fetchVariants(selectedBrand, selectedModel)
    }
  }, [selectedModel])

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error("Please login to continue")
      openAuth("login")
      return
    }

    if (isLaunchLocked(product)) {
      toast.error(product.launch_badge || "This product is launching soon")
      return
    }

    if (product.product_type === 'combo' || product.item_type === 'combo') {
      navigate(`/products/${product.id}`)
      return
    }

    // Check if product requires color/size selection
    const hasColors = product.colors && product.colors.length > 0
    const hasSizes = product.sizes && product.sizes.length > 0
    const hasVariantStock = product.variant_stock && product.variant_stock.length > 0
    
    // If product has variants, navigate to product details instead of adding directly
    if (hasColors || hasSizes || hasVariantStock) {
      navigate(`/products/${product.id}`)
      return
    }

    try {
      await api.post("/cart/add", {
        product_id: product.id,
        quantity: 1,
      })

      const variantInfo = [product.color].filter(Boolean).join(', ');
      toast.success(`${product.name}${variantInfo ? ` (${variantInfo})` : ''} added to cart!`)
      refreshCartCount()
    } catch (error) {
      console.error("Add to cart error:", error)
      toast.error(error.response?.data?.detail || "Failed to add to cart")
    }
  }

  const handleWishlistToggle = async (product) => {
    if (!isAuthenticated) {
      toast.error("Please login to continue")
      openAuth("login")
      return
    }

    const wasWishlisted = isInWishlist(product.id)
    const nextAction = wasWishlisted ? removeFromWishlist : addToWishlist
    const success = await nextAction(product.id)
    if (success) {
      toast.success(wasWishlisted ? "Removed from wishlist" : "Added to wishlist")
    } else {
      toast.error("Unable to update wishlist")
    }
  }

  const fetchData = async () => {
    try {
      const homeRes = await axios.get(`${API}/homepage`, {
        params: {
          featured_limit: 15,
          section_limit: 30,
          combo_limit: 30,
        },
      })
      const data = homeRes.data || {}
      setBrands(data.brands || [])
      setCategories(data.categories || [])
      
      // Separate Outran products into purchasable and launching
      const allOutranProducts = data.outran_products || []
      const purchasableOutran = allOutranProducts.filter(p => !isLaunchLocked(p))
      const launchingOutran = allOutranProducts.filter(p => isLaunchLocked(p))
      
      setProducts(purchasableOutran)
      setLaunchingProducts(launchingOutran)
      setBestSellers(data.best_sellers || [])
      setComboOffers(data.combo_products || [])
      setFeaturedProducts((data.featured_products || []).slice(0, 15))
      setAllProducts(data.all_products || [])
      setNewArrivals(data.new_arrivals || [])
    } catch (e) {
      console.error('[v0] Error fetching data:', e)
    } finally {
      setDataLoaded(true)
    }
  }
  const fetchModels = async (brand) => {
    try {
      const res = await axios.get(`${API}/bikes/models/${encodeURIComponent(brand)}`)
      setModels(res.data || [])
      setSelectedModel('')
      setVariants([])
      setSelectedVariant('')
    } catch (e) {
      console.error('[v0] Error fetching models:', e)
    }
  }

  const fetchVariants = async (brand, model) => {
    try {
      const res = await axios.get(
        `${API}/bikes/variants/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`
      )
      setVariants(res.data || [])
      setSelectedVariant('')
    } catch (e) {
      console.error('[v0] Error fetching variants:', e)
    }
  }

  const handleExploreGear = () => {
    if (selectedBrand && selectedModel && selectedVariant) {
      setBikeCompatibility(selectedBrand, selectedModel, selectedVariant)
      navigate('/products?product_type=gear')
    }
  }

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 400
      if (direction === 'left') {
        categoryScrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
      } else {
        categoryScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
      }
    }
  }

  const scrollBrands = (direction) => {
    if (brandScrollRef.current) {
      const scrollAmount = 300
      if (direction === 'left') {
        brandScrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
      } else {
        brandScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
      }
    }
  }

  const carouselClass = 'flex flex-nowrap gap-4 md:gap-6 overflow-x-auto overflow-y-hidden scrollbar-hide pb-2 -mx-1 px-1'
  const carouselClassWithPartial = 'flex flex-nowrap gap-4 md:gap-6 overflow-x-auto overflow-y-hidden scrollbar-hide pb-2 pr-[calc(16%+12px)] md:pr-[calc(18%+24px)]'

  const SectionHeader = ({ title, accent, subtitle, disabled = false }) => (
    <div className={`mb-6 text-center ${disabled ? 'opacity-40' : ''}`}>
      <h2
        className="text-4xl md:text-5xl font-black uppercase text-white tracking-wider"
        style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.1em" }}
      >
        {title} <span className={disabled ? 'text-zinc-500' : 'text-[#ffcc32]'}>{accent}</span>
      </h2>
      <p className={`text-sm md:text-base mt-2 tracking-wide ${disabled ? 'text-zinc-600' : 'text-zinc-400'}`}>
        {subtitle}
      </p>
    </div>
  )

  const renderProductCard = (product, index, options = {}) => {
    // Determine badge based on product attributes, not section
    let badge = ''
    let badgeClass = 'bg-red-600 text-white'
    
    // If forceBadge is provided, use it instead of product attributes
    if (options.forceBadge) {
      badge = options.forceBadge
      badgeClass = options.forceBadgeClass || 'bg-red-600 text-white'
    } else if (product.product_type === 'combo' || product.item_type === 'combo') {
      badge = 'Combo'
      badgeClass = 'bg-[#ffcc32] text-black'
    } else if (product.is_outran) {
      badge = 'Outran'
      badgeClass = 'bg-[#ffcc32] text-black'
    } else if (product.is_best_seller) {
      badge = 'Best Seller'
      badgeClass = 'bg-red-600 text-white'
    } else if (product.is_featured) {
      badge = 'Featured'
      badgeClass = 'bg-red-600 text-white'
    } else if (options.badge) {
      // Fall back to section-specific badge if no product attribute matches
      badge = options.badge
      badgeClass = options.badgeClass || 'bg-red-600 text-white'
    }
    
    return (
      <ProductCard
        key={product.id}
        product={product}
        badge={badge}
        badgeClass={badgeClass}
        layout={options.layout || 'carousel'}
        showStock={options.showStock !== false}
        showBadge={options.showBadge !== false}
        isWishlisted={isInWishlist(product.id)}
        onOpen={(p) => navigate(`/products/${p.id}`)}
        onAddToCart={handleAddToCart}
        onWishlistToggle={handleWishlistToggle}
        onLaunchBlocked={(p, launch) => toast.error(launch?.badge || p.launch_badge || 'Launching soon')}
      />
    )
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Navbar spacing */}
      <div className="h-16 md:h-[108px]" />

      {/* HERO SECTION */}

     {/* ================= MOBILE HERO ================= */}
{/* ================= MOBILE HERO ================= */}
<section className="relative md:hidden overflow-hidden">
  <div className="relative w-full h-[560px] min-[390px]:h-[610px] overflow-hidden">

    {heroSlides.map((slide, index) => (
      <motion.div
        key={`mobile-${slide.src}`}
        className="absolute inset-0"
        animate={{ opacity: heroIndex === index ? 1 : 0 }}
        transition={{ duration: 0.7 }}
      >
        {slide.type === "video" ? (
          <video
            className="h-full w-full object-cover object-[62%_center] scale-[1.03]"
            src={slide.src}
            poster={slide.poster}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <div
            className="h-full w-full bg-cover bg-[62%_center] scale-[1.03]"
            style={{ backgroundImage: `url("${slide.src}")` }}
          />
        )}
      </motion.div>
    ))}

    {/* ---------- Overlay ---------- */}

    {/* Tiny dark layer */}
    <div className="absolute inset-0 bg-black/10" />

    {/* Bottom fade only */}
    <div className="absolute inset-x-0 bottom-0 h-[28%] bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

    <div className="relative z-20 flex h-full flex-col justify-between px-6 pt-[75px] pb-7">

      {/* ---------------- TOP ---------------- */}

      <div>

        <div className="inline-flex items-center gap-2 rounded-full border border-[#ffcc32]/70 bg-black/20 backdrop-blur-md px-4 py-2">
          <div className="h-2.5 w-2.5 rounded-full bg-[#ffcc32]" />
          <span className="text-[10px] font-semibold uppercase tracking-[3px] text-[#ffcc32]">
            {heroSlides[heroIndex].eyebrow}
          </span>
        </div>

        <h1
          className="mt-5 uppercase text-white leading-[0.92]"
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "clamp(2rem,10vw,3.2rem)",
            textShadow: "0 2px 12px rgba(0,0,0,.35)"
          }}
        >
          {heroSlides[heroIndex].title} <span className="text-[#ffcc32]">
            {heroSlides[heroIndex].accent}
          </span>
        </h1>

        <p className="mt-4 max-w-[280px] text-[15px] leading-7 text-white/85">
          {heroSlides[heroIndex].copy}
        </p>

      </div>

      {/* ---------------- BOTTOM ---------------- */}

      <div className="space-y-4">

        <div className="flex gap-3">
          <button
            onClick={heroSlides[heroIndex].action}
            className="group flex h-[58px] flex-1 items-center justify-between rounded-2xl bg-[#ffcc32] px-6 font-bold text-black shadow-[0_15px_35px_rgba(255,204,50,.28)]"
          >
            <span className="tracking-wide">
              {heroSlides[heroIndex].primary}
            </span>

            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-[#ffcc32]">
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            </div>
          </button>

          <button
            onClick={() => navigate('/products')}
            className="group flex h-[58px] flex-1 items-center justify-center rounded-2xl bg-white/10 border border-white/20 px-6 font-bold text-white backdrop-blur-sm"
          >
            <span className="tracking-wide">
              Explore
            </span>
          </button>
        </div>

        {heroSlides[heroIndex].subtitle && (
          <p className="text-center text-zinc-300 text-xs font-medium tracking-wide">
            {heroSlides[heroIndex].subtitle}
          </p>
        )}

      </div>

      {/* ---------------- DOTS ---------------- */}

      

    </div>
  </div>
</section>

      {/* ================= DESKTOP HERO ================= */}
      <section className="relative hidden md:block pt-2 pb-8 px-3 lg:px-6">
        <div
          className="relative w-full max-w-[1400px] mx-auto h-[460px] lg:h-[500px] rounded-[28px] overflow-hidden border border-[#35533f]/40 shadow-[0_20px_80px_rgba(0,0,0,.35)]"
        >
          {heroSlides.map((slide, index) => (
            <motion.div
              key={`desktop-${slide.src}`}
              className="absolute inset-0"
              animate={{ opacity: heroIndex === index ? 1 : 0 }}
              transition={{ duration: 0.7 }}
            >
              {slide.type === 'video' ? (
                <video
                  className="h-full w-full object-cover object-[center_42%]"
                  src={slide.src}
                  poster={slide.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <div
                  className="h-full w-full bg-cover bg-[center_42%]"
                  style={{ backgroundImage: `url("${slide.src}")` }}
                />
              )}
            </motion.div>
          ))}

    <div className="absolute inset-0 bg-black/10" />
    <div className="absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-t from-[#09120E]/75 via-[#09120E]/20 to-transparent" />

    <div className="relative z-20 h-full px-10 lg:px-12 py-10 flex items-center justify-between gap-12">
      <div className="max-w-[480px]">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ffcc32]/20 bg-[#ffcc32]/10 mb-6 backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-[#ffcc32]" />
          <span className="text-[#FFD36B] tracking-[3px] uppercase text-xs">
            {heroSlides[heroIndex].eyebrow}
          </span>
        </div>

        <h1
          className="uppercase text-[#fff] leading-[0.9]"
          style={{
            fontFamily: "Cinzel, serif",
            fontSize: "clamp(2rem,4vw,3.5rem)",
            textShadow: "0 4px 16px rgba(0,0,0,.25)",
          }}
        >
          {heroSlides[heroIndex].title} <span className="text-[#ffcc32]">
            {heroSlides[heroIndex].accent}
          </span>
        </h1>

        <p className="mt-5 text-[#d8e7da] text-[1rem] leading-7 max-w-[450px]">
          {heroSlides[heroIndex].copy}
        </p>

        <div className="flex flex-col gap-3 mt-8">
          <div className="flex gap-3">
            <button
              onClick={heroSlides[heroIndex].action}
              className="h-12 flex-1 px-6 rounded-xl bg-[#ffcc32] text-black font-black text-sm shadow-[0_15px_35px_rgba(255,204,50,.28)]"
            >
              {heroSlides[heroIndex].primary}
            </button>
            <button
              onClick={() => navigate('/products')}
              className="h-12 flex-1 px-6 rounded-xl bg-white/10 border border-white/20 text-white font-black text-sm backdrop-blur-sm"
            >
              Explore
            </button>
          </div>
          {heroSlides[heroIndex].subtitle && (
            <p className="text-zinc-300 text-xs font-medium tracking-wide">
              {heroSlides[heroIndex].subtitle}
            </p>
          )}
        </div>
      </div>

      <div className="absolute bottom-6 right-8 flex gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={`desktop-dot-${slide.src}`}
            onClick={() => setHeroIndex(index)}
            className={`h-1.5 rounded-full transition-all ${
              heroIndex === index
                ? "w-10 bg-[#ffcc32]"
                : "w-5 bg-white/35"
            }`}
            aria-label={`Hero slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  </div>
</section>

      

      {/* ================= OUTRAN ORIGINALS ================= */}
      {products.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 py-4 md:py-6 bg-transparent border-y border-white/10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Outran"
              accent="Originals"
              subtitle="Premium Quality • Exclusive Design • Best Performance"
            />
            <div className={carouselClassWithPartial}>
              {products.map((product, index) =>
  renderProductCard(product, index, {
    forceBadge: 'Outran',
    forceBadgeClass: 'bg-[#ffcc32] text-black',
    showStock: false,
    showBadge: true,
  })
)}
            </div>
          </div>
        </section>
      )}

      {/* ================= LAUNCHING SOON ================= */}
      {launchingProducts.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 py-4 md:py-6 bg-transparent border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Launching"
              accent="Soon"
              subtitle="Coming Soon • Exclusive Drops • Be First"
            />
            <div className={carouselClassWithPartial}>
              {launchingProducts.map((product, index) =>
                renderProductCard(product, index, {
                  forceBadge: product.launch_badge || 'Coming Soon',
                  forceBadgeClass: 'bg-[#ffcc32] text-black',
                  showStock: false,
                  showBadge: true,
                })
              )}
            </div>
          </div>
        </section>
      )}

      {/* ================= BEST SELLERS SECTION ================= */}
      {bestSellers.length > 0 && (
        <section className="pt-4 pb-3 md:pt-6 md:pb-4 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Best"
              accent="Sellers"
              subtitle="Top Rated • Customer Favorites • Must Have"
            />
            <div className={carouselClassWithPartial}>
              {bestSellers.map((product, index) =>
                renderProductCard(product, index, {})
              )}
            </div>
          </div>
        </section>
      )}

      {/* ================= COMBO PRODUCTS ================= */}
      {comboOffers.length > 0 && (
        <section className="pt-4 pb-3 md:pt-6 md:pb-4 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Combo"
              accent="Offers"
              subtitle="Best Value • Bundled Deals • Save More"
            />
            <div className={carouselClassWithPartial}>
              {comboOffers.map((product, index) =>
                renderProductCard(product, index, {})
              )}
            </div>
          </div>
        </section>
      )}

      {/* ================= FEATURED PRODUCTS ================= */}
      {featuredProducts.length > 0 && (
  <section className="pt-4 pb-3 md:pt-6 md:pb-4 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-white/10">
    <div className="max-w-7xl mx-auto">
      <SectionHeader
        title="Featured"
        accent="Products"
        subtitle="Handpicked • Premium Quality • Top Picks"
      />

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
        {featuredProducts.map((product, index) =>
          renderProductCard(product, index, {
            layout: "grid",
          })
        )}
      </div>
    </div>
  </section>
)}
      {/* ================= NEW ARRIVALS ================= */}
      {/* {newArrivals.length > 0 && (
        <section className="pt-4 pb-3 md:pt-6 md:pb-4 px-4 sm:px-6 lg:px-8 bg-transparent border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="New"
              accent="Arrivals"
              subtitle="Fresh Drops • Latest Launches"
            />
            <div className={carouselClassWithPartial}>
              {newArrivals.map((product, index) =>
                renderProductCard(product, index, {
                  badge: 'New',
                  badgeClass: 'bg-[#ffcc32] text-black',
                })
              )}
            </div>
          </div>
        </section>
      )} */}

      {/* ================= ALL PRODUCTS =================
      {allProducts.length > 0 && (
        <section className="pt-4 pb-6 md:pt-6 md:pb-8 px-4 sm:px-6 lg:px-8 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="All"
              accent="Products"
              subtitle="Complete Collection • Everything You Need"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {allProducts.map((product, index) =>
                renderProductCard(product, index, {
                  badge: product.product_type === 'combo' ? 'Combo' : 
                         product.is_outran ? 'Outran' : 
                         product.is_best_seller ? 'Best Seller' : '',
                  badgeClass: product.product_type === 'combo' ? 'bg-[#ffcc32] text-black' : 
                               product.is_outran ? 'bg-purple-600 text-white' : 
                               product.is_best_seller ? 'bg-red-600 text-white' : 'bg-gray-600 text-white',
                })
              )}
            </div>
          </div>
        </section>
      )} */}
    </div>

  )

}

export default Home
